// C16/C17 pdf-lib stamping pivot -- see PdfDocument.ts / TargetPdfDocument.ts
// for the background. This module does the actual work: fetch the real
// source PDF, find every blank via pdfAnchors, embed a Latin-Extended-A
// capable font (Poppins -- reusing the same .ttf files frontend/src/utils/
// fonts.tsx already registers for the react-pdf reports, so we're not
// adding a new font dependency), draw each value, and hand back the bytes.
//
// pdf-lib draws the real PDF as-is -- the only thing that changes is the
// handful of values we stamp on top, so the source's own layout, its bold
// "Raben" wordmark, and the "YOUR PARTNER IN LOGISTICS" footer all come
// through untouched. Only the drawn values need a custom font: the source
// document's own text is already correctly embedded by whoever authored
// it, but pdf-lib's/react-pdf's built-in fallback fonts have no Polish
// glyphs, and a carrier's company name can contain them ("Chłodnia",
// "Śląskie", ...).

import { PDFDocument, PDFFont, PDFPage, rgb } from 'pdf-lib';
// eslint-disable-next-line import/no-extraneous-dependencies
import fontkit from '@pdf-lib/fontkit';
import { GlobalWorkerOptions } from 'pdfjs-dist/legacy/build/pdf';
import {
  AnchorSpec,
  ResolvedAnchor,
  getPageLines,
  findAnchor,
} from './pdfAnchors';

// pdfjs-dist always parses through a "worker" abstraction (even when the
// result -- getTextContent -- never touches a canvas). In the browser it
// refuses to fall back to running in the main thread unless
// GlobalWorkerOptions.workerSrc is set to something it can actually load,
// so the matching pdf.worker.min.js from the SAME pdfjs-dist version is
// committed as a static asset (frontend/public/assets/pdfjs), the same way
// the Raben source PDFs are served as static assets. If pdfjs-dist is ever
// upgraded, this file must be re-copied from
// node_modules/pdfjs-dist/legacy/build/pdf.worker.min.js or text extraction
// will fail at runtime (the API/worker versions are checked and must
// match).
GlobalWorkerOptions.workerSrc = '/assets/pdfjs/pdf.worker.min.js';

const VALUE_FONT_SIZE = 11;
const ANCHOR_GAP = 3; // pt of breathing room between a drawn value and its anchor phrase
// Client feedback 2026-08-27: drawing at the anchor's own baseline ran the
// dotted leaders straight through the glyphs. Lift the stamped value a
// touch so it sits ON the dotted line rather than crossed out by it --
// small enough to still read as filling that line, not floating above it.
const BASELINE_LIFT = 2.5;
const TEXT_COLOR = rgb(26 / 255, 26 / 255, 26 / 255); // matches the source page's body text (#1a1a1a)

// RG 28.08 12.1: long values (a carrier's company name is also stamped into
// "Signature:", which starts at x~384 and so has only ~140pt to the right
// margin) used to run off the page. A value now shrinks in FONT_SIZE_STEP
// steps down to MIN_VALUE_FONT_SIZE, then wraps onto further lines at that
// size, and only when even that cannot fit does stamping fail -- a signed
// document must never carry a clipped or truncated name.
const MIN_VALUE_FONT_SIZE = 7;
const FONT_SIZE_STEP = 0.5;
const MAX_VALUE_LINES = 3;
const LINE_HEIGHT_FACTOR = 1.2;
// Every Raben page is laid out with 70.8pt side margins (the text block
// runs x 70.8 .. 524.5 on the 595.3pt wide A4 page).
const PAGE_SIDE_MARGIN = 70.8;
// Wrapped lines must stay clear of the red "YOUR PARTNER IN LOGISTICS"
// block in the bottom right corner of every page (its top edge is at
// y~85.8pt).
const PAGE_BOTTOM_LIMIT = 92;

export interface PdfDraw {
  spec: AnchorSpec;
  value: string;
}

export interface StampPdfOptions {
  sourceUrl: string;
  pageNumber: number; // 1-indexed, matches pdfjs' getPage
  draws: ReadonlyArray<PdfDraw>;
  fontUrl: string;
}

async function fetchBytes(url: string): Promise<Uint8Array> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(
      `Supplier Commitment PDF: failed to fetch ${url} (${response.status})`
    );
  }
  const buffer = await response.arrayBuffer();
  return new Uint8Array(buffer);
}

// Width of `text` at `size` pt (font.widthOfTextAtSize in production).
export interface TextMeasure {
  (text: string, size: number): number;
}

export interface FittedValue {
  size: number;
  lines: string[];
}

// Splits `text` into lines no wider than `maxWidth`: greedy by words, with a
// word that is wider than a whole line broken between characters. Always
// takes at least one character per line, so it terminates for any width.
function wrapText(
  text: string,
  maxWidth: number,
  measure: (text: string) => number
): string[] {
  const lines: string[] = [];
  let current = '';
  const pushLongWord = (word: string): string => {
    let rest = word;
    while (measure(rest) > maxWidth && rest.length > 1) {
      let cut = rest.length - 1;
      while (cut > 1 && measure(rest.slice(0, cut)) > maxWidth) cut -= 1;
      lines.push(rest.slice(0, cut));
      rest = rest.slice(cut);
    }
    return rest;
  };
  text
    .split(/\s+/)
    .filter(Boolean)
    .forEach((word) => {
      const candidate = current ? `${current} ${word}` : word;
      if (measure(candidate) <= maxWidth) {
        current = candidate;
        return;
      }
      if (current) lines.push(current);
      current = pushLongWord(word);
    });
  if (current) lines.push(current);
  return lines;
}

// Picks the font size and line breaks that fit `value` into `maxWidth`:
// the largest size (<= VALUE_FONT_SIZE) at which it stays on one line, else
// MIN_VALUE_FONT_SIZE with wrapping. Pure -- `measure` is
// font.widthOfTextAtSize in production -- so it is unit-testable.
export function fitValue(
  value: string,
  maxWidth: number,
  measure: TextMeasure
): FittedValue {
  for (
    let size = VALUE_FONT_SIZE;
    size >= MIN_VALUE_FONT_SIZE;
    size -= FONT_SIZE_STEP
  ) {
    if (measure(value, size) <= maxWidth) {
      return { size, lines: [value] };
    }
  }
  return {
    size: MIN_VALUE_FONT_SIZE,
    lines: wrapText(value, maxWidth, (text) =>
      measure(text, MIN_VALUE_FONT_SIZE)
    ),
  };
}

// Draws one value at its anchor, shrinking/wrapping it to stay inside the
// page's side margins. 'after'/'afterBlank' values grow rightwards from the
// anchor, 'before' values are right-aligned to end ahead of it (they never
// wrap -- only the short numeric blanks use that mode).
function drawValue(
  page: PDFPage,
  font: PDFFont,
  spec: AnchorSpec,
  anchor: ResolvedAnchor,
  value: string
): void {
  const measure: TextMeasure = (text, size) =>
    font.widthOfTextAtSize(text, size);
  const before = spec.mode === 'before';
  const startX = before ? PAGE_SIDE_MARGIN : anchor.x + ANCHOR_GAP;
  const endX = before
    ? anchor.x - ANCHOR_GAP
    : page.getWidth() - PAGE_SIDE_MARGIN;
  const fitted = fitValue(value, endX - startX, measure);

  const baseline = anchor.y + BASELINE_LIFT;
  const lineHeight = fitted.size * LINE_HEIGHT_FACTOR;
  const lastBaseline = baseline - (fitted.lines.length - 1) * lineHeight;
  const tooWide = fitted.lines.some(
    (line) => measure(line, fitted.size) > endX - startX
  );
  if (
    tooWide ||
    fitted.lines.length > MAX_VALUE_LINES ||
    (fitted.lines.length > 1 && before) ||
    lastBaseline < PAGE_BOTTOM_LIMIT
  ) {
    throw new Error(
      `Supplier Commitment PDF: value does not fit its blank (${value.length} characters at "${spec.phrase}"). ` +
        'Refusing to stamp a clipped or overlapping value into a document a carrier signs.'
    );
  }
  fitted.lines.forEach((line, index) => {
    page.drawText(line, {
      x: before ? endX - measure(line, fitted.size) : startX,
      y: baseline - index * lineHeight,
      size: fitted.size,
      font,
      color: TEXT_COLOR,
    });
  });
}

// Fetches the real source PDF, locates every draw's anchor in its text
// layer, stamps the values on with an embedded Poppins, and returns the
// finished document's bytes.
export async function stampPdf(options: StampPdfOptions): Promise<Uint8Array> {
  const { sourceUrl, pageNumber, draws, fontUrl } = options;

  const [sourceBytes, fontBytes] = await Promise.all([
    fetchBytes(sourceUrl),
    fetchBytes(fontUrl),
  ]);

  // pdfjs-dist and pdf-lib each parse the document independently -- give
  // each its own copy of the bytes so neither can affect the other.
  const lines = await getPageLines(sourceBytes.slice(), pageNumber);

  const pdfDoc = await PDFDocument.load(sourceBytes.slice());
  pdfDoc.registerFontkit(fontkit);
  const font = await pdfDoc.embedFont(fontBytes, { subset: true });
  const page = pdfDoc.getPage(pageNumber - 1);

  for (const draw of draws) {
    const anchor = findAnchor(lines, draw.spec);
    drawValue(page, font, draw.spec, anchor, draw.value);
  }

  return pdfDoc.save();
}

// Same download hand-off both documents used before this pivot (create an
// object URL, click a throwaway <a download>, revoke it) -- only the bytes
// now come from pdf-lib's save() instead of @react-pdf/renderer's
// pdf(...).toBlob().
export function downloadPdfBytes(bytes: Uint8Array, filename: string): void {
  // eslint-disable-next-line no-undef -- BlobPart is a DOM lib type, not a runtime global
  const blob = new Blob([bytes as BlobPart], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
